import type { AppLocale } from './index'

export const INTL_LOCALE: Record<AppLocale, string> = {
  it: 'it-IT',
  en: 'en-GB',
}

export type DateValue = Date | string | number

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}

const DEFAULT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
}

const DEFAULT_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...DEFAULT_DATE_OPTIONS,
  ...DEFAULT_TIME_OPTIONS,
}

function asDate(value: DateValue): Date | null {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Numeric dates follow the active locale explicitly (D-050): `dd/mm/yyyy` for
 * Italian and `mm/dd/yyyy` for English, independent of the host locale.
 * Options that ask for names (`weekday`, long/short month) keep Intl
 * localization instead.
 */
function isNumericDateOptions(options: Intl.DateTimeFormatOptions): boolean {
  if (options.dateStyle || options.timeStyle) return false
  if (options.weekday || options.era) return false
  if (
    options.month &&
    options.month !== 'numeric' &&
    options.month !== '2-digit'
  ) {
    return false
  }
  return (
    options.day !== undefined ||
    options.month !== undefined ||
    options.year !== undefined
  )
}

function numericDateParts(
  date: Date,
  timeZone: string | undefined,
): { day: string; month: string; year: string } {
  // The source locale of the parts is irrelevant: only their type is used, so
  // the rendered order below is deterministic on every host.
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone,
  }).formatToParts(date)
  const value = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? ''
  return {
    day: value('day'),
    month: value('month'),
    year: value('year'),
  }
}

function formatNumericDate(
  date: Date,
  locale: AppLocale,
  options: Intl.DateTimeFormatOptions,
): string {
  const parts = numericDateParts(date, options.timeZone)
  const day = options.day !== undefined ? parts.day : null
  const month = options.month !== undefined ? parts.month : null
  const year = options.year !== undefined ? parts.year : null
  const ordered = locale === 'it' ? [day, month, year] : [month, day, year]
  return ordered.filter((part): part is string => part !== null).join('/')
}

function timeOptions(
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions | null {
  if (
    options.hour === undefined &&
    options.minute === undefined &&
    options.second === undefined &&
    options.timeZoneName === undefined
  ) {
    return null
  }
  return {
    hour: options.hour,
    minute: options.minute,
    second: options.second,
    fractionalSecondDigits: options.fractionalSecondDigits,
    timeZoneName: options.timeZoneName,
    hour12: options.hour12,
    hourCycle: options.hourCycle,
    timeZone: options.timeZone,
  }
}

export function formatDate(
  value: DateValue,
  locale: AppLocale,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS,
): string {
  const date = asDate(value)
  if (!date) return ''
  if (isNumericDateOptions(options)) {
    return formatNumericDate(date, locale, options)
  }
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], options).format(date)
}

export function formatTime(
  value: DateValue,
  locale: AppLocale,
  options: Intl.DateTimeFormatOptions = DEFAULT_TIME_OPTIONS,
): string {
  const date = asDate(value)
  if (!date) return ''
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], options).format(date)
}

/**
 * Numeric date plus localized time (D-050), e.g. `25/10/2026, 18:00` in Italian
 * and `10/25/2026, 18:00` in English. Long-format options continue to use Intl.
 */
export function formatDateTime(
  value: DateValue,
  locale: AppLocale,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_TIME_OPTIONS,
): string {
  if (!isNumericDateOptions(options)) {
    return formatDate(value, locale, options)
  }
  const date = asDate(value)
  if (!date) return ''
  const dateText = formatNumericDate(date, locale, options)
  const time = timeOptions(options)
  return time ? `${dateText}, ${formatTime(date, locale, time)}` : dateText
}

export function formatMonthYear(
  value: DateValue,
  locale: AppLocale,
): string {
  return formatDate(value, locale, { month: 'long', year: 'numeric' })
}

export function formatWeekdayShort(
  value: DateValue,
  locale: AppLocale,
): string {
  return formatDate(value, locale, { weekday: 'short' })
}

export function formatNumber(
  value: number,
  locale: AppLocale,
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(INTL_LOCALE[locale], {
    useGrouping: true,
    ...options,
  }).format(value)
}

export function formatCurrency(
  value: number,
  locale: AppLocale,
  currency = 'EUR',
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(INTL_LOCALE[locale], {
    useGrouping: true,
    style: 'currency',
    currency,
    ...options,
  }).format(value)
}

/**
 * Relative timestamps for notifications. API timestamps are ISO 8601 UTC;
 * display always uses the active locale.
 */
export function formatRelativeTime(
  value: DateValue,
  locale: AppLocale,
  now: DateValue = Date.now(),
): string {
  const date = asDate(value)
  const reference = asDate(now)
  if (!date || !reference) return ''

  const formatter = new Intl.RelativeTimeFormat(INTL_LOCALE[locale], {
    numeric: 'auto',
  })
  const deltaSeconds = Math.round((date.getTime() - reference.getTime()) / 1000)
  const absolute = Math.abs(deltaSeconds)

  if (absolute < 60) return formatter.format(deltaSeconds, 'second')
  if (absolute < 3_600) {
    return formatter.format(Math.round(deltaSeconds / 60), 'minute')
  }
  if (absolute < 86_400) {
    return formatter.format(Math.round(deltaSeconds / 3_600), 'hour')
  }
  if (absolute < 604_800) {
    return formatter.format(Math.round(deltaSeconds / 86_400), 'day')
  }
  if (absolute < 2_629_800) {
    return formatter.format(Math.round(deltaSeconds / 604_800), 'week')
  }
  if (absolute < 31_557_600) {
    return formatter.format(Math.round(deltaSeconds / 2_629_800), 'month')
  }
  return formatter.format(Math.round(deltaSeconds / 31_557_600), 'year')
}
